import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEventosGlobal } from '@/api/queries/eventos';
import { Skeleton } from '@/components/ui/skeleton';

const tipoIcon: Record<string, string> = {
  mensagem_enviada: '↗',
  mensagem_recebida: '↘',
  ligacao_feita: '✆',
  ligacao_recebida: '✆',
  email_enviado: '✉',
  email_recebido: '✉',
  call_agendada: '◷',
  call_realizada: '●',
  call_no_show: '✗',
  audit_criado: '◆',
  audit_revisado: '◇',
  nota_adicionada: '✎',
  status_mudou: '⟳',
  lead_ops_bridge: '⇄',
  proposta_enviada: '⊟',
  proposta_aceita: '✓',
  proposta_recusada: '✗',
};

const CANAIS = ['', 'whatsapp', 'email', 'ligacao', 'instagram', 'linkedin', 'presencial', 'sistema'];

function relativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (seconds < 60) return 'agora';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export function EventosPage() {
  const [canal, setCanal] = useState('');
  const { data, isLoading } = useEventosGlobal({ canal: canal || undefined, limit: 200 });

  let lastDate = '';

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between border-b border-border/70 pb-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Eventos</h1>
          <p className="mt-0.5 text-xs text-muted-foreground tabular">
            <span className="mono">{data?.length ?? 0}</span> registros recentes
          </p>
        </div>
        <select
          value={canal}
          onChange={(e) => setCanal(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm transition-colors hover:bg-muted"
        >
          {CANAIS.map((c) => (
            <option key={c || 'todos'} value={c}>
              {c || 'todos canais'}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum evento registrado.</p>
      ) : (
        <div className="space-y-1">
          {data.map((ev) => {
            const d = new Date(ev.data_evento);
            const dayKey = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
            const showHeader = dayKey !== lastDate;
            lastDate = dayKey;
            return (
              <div key={ev.id}>
                {showHeader && (
                  <div className="mt-4 mb-1 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                    {dayKey}
                  </div>
                )}
                <div className="group grid grid-cols-[auto_1fr_auto] items-start gap-3 border-b border-border/50 px-2 py-2 hover:bg-muted/40 transition-colors">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/70 bg-card text-xs">
                    {tipoIcon[ev.tipo] ?? '·'}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[13px]">
                      <span className="font-medium lowercase">{ev.tipo.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground/50">·</span>
                      <Link to={`/empresas/${ev.empresa_id}`} className="hover:underline truncate">
                        {ev.empresa_nome}
                      </Link>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="text-[11px] text-muted-foreground lowercase">{ev.canal}</span>
                    </div>
                    {ev.payload && typeof ev.payload.resumo === 'string' && ev.payload.resumo && (
                      <p className="mt-0.5 text-[12px] text-muted-foreground line-clamp-2">
                        {ev.payload.resumo}
                      </p>
                    )}
                  </div>
                  <span
                    className="mono shrink-0 text-[11px] text-muted-foreground tabular"
                    title={d.toLocaleString('pt-BR')}
                  >
                    {relativeTime(d)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
